import { useEffect, useState } from "react";
import { API } from "./api";
import {
  Grid, Card, CardContent, Typography, Button
} from "@mui/material";

import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from "recharts";

export default function URLMonitoring() {
  const [urls, setUrls] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [filterType, setFilterType] = useState("ALL");

  // 🔥 FETCH
  const fetchUrls = async () => {
    try {
      const res = await API.get("/apps");
      const data = res?.data?.data || res?.data || [];
      setUrls(Array.isArray(data) ? data : []);
    } catch {
      setUrls([]);
    }
  };

  const getUniqueUrls = (data) => {
    const map = {};
    (data || []).forEach((item) => {
      if (item?.url && !map[item.url]) {
        map[item.url] = item;
      }
    });
    return Object.values(map);
  };

  // 🔥 STATUS CHECK
  const checkStatus = async () => {
    const activeUrls = urls.filter((u) => u.url);
    const uniqueUrls = getUniqueUrls(activeUrls);

    const results = await Promise.all(
      uniqueUrls.map(async (app) => {
        try {
          const res = await API.get(
            `/check-url?url=${encodeURIComponent(app.url)}&health_urls=${encodeURIComponent(app.health_urls || "")}&app_name=${encodeURIComponent(app.app_name)}`
          );

          return {
            ...app,
            endpoint: app.url,
            state: res?.data?.state,
            response_time: res?.data?.response_time || 0,
            failure_count: res?.data?.failure_count || 0
          };
        } catch {
          return {
            ...app,
            endpoint: app.url,
            state: "DOWN",
            response_time: 0,
            failure_count: 1
          };
        }
      })
    );

    const currentSet = new Set(activeUrls.map((u) => u.url));
    const filtered = results.filter((r) => currentSet.has(r.url));

    // 🔥 HISTORY
    setHistory((prev) => {
      const logs = filtered.map((r) => ({
        app: r.app_name,
        url: r.endpoint,
        state: r.state,
        time: new Date().toLocaleTimeString(),
        response: r.response_time,
        failure: r.failure_count
      }));

      return [...logs, ...prev].slice(0, 100);
    });

    setStatusData(filtered);
  };

  // 🔥 INITIAL LOAD
  useEffect(() => {
    fetchUrls();
  }, []);

  useEffect(() => {
    if (urls.length > 0) {
      checkStatus();
    }
  }, [urls]);

  // 🔥 REAL-TIME AUTO REFRESH (EVERY 5 SEC)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUrls();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ✅ INSTANT COUNT (NO DELAY)
  const uniqueUrlsInstant = getUniqueUrls(urls);
  const instantTotal = uniqueUrlsInstant.length;

  const uniqueStatus = getUniqueUrls(statusData);

  const upUrls = uniqueStatus.filter((u) => u.state === "UP");
  const downUrls = uniqueStatus.filter((u) => u.state === "DOWN");
  const partialUrls = uniqueStatus.filter((u) => u.state === "PARTIAL");

  const total = uniqueStatus.length || 1;

  const filteredList =
    filterType === "UP"
      ? upUrls
      : filterType === "DOWN"
      ? downUrls
      : filterType === "PARTIAL"
      ? partialUrls
      : uniqueStatus;

  const chartData = [
    { name: "UP", value: upUrls.length },
    { name: "PARTIAL", value: partialUrls.length },
    { name: "DOWN", value: downUrls.length },
  ];

  const COLORS = ["#10b981", "#f59e0b", "#ef4444"];

  // 🔥 BLINK STYLE
  const blinkStyle = {
    animation: "blink 1s infinite"
  };

  return (
    <div style={{ padding: 20 }}>
      <style>
        {`
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
        `}
      </style>

      <h2>🌐 AI URL Monitoring Dashboard</h2>

      <Button onClick={() => setShowHistory(!showHistory)}>
        {showHistory ? "⬅ Back" : "📜 View History"}
      </Button>

      {/* HISTORY */}
      {showHistory && (
        <div style={{
          maxHeight: 450,
          overflowY: "auto",
          background: "#020617",
          padding: 15,
          borderRadius: 10,
          color: "#22c55e",
          fontFamily: "monospace",
          marginTop: 10
        }}>
          {history.map((h, i) => {
            const isAlert = h.failure >= 3;

            return (
              <div key={i} style={{
                color: isAlert
                  ? "#ef4444"
                  : h.state === "UP"
                  ? "#22c55e"
                  : h.state === "PARTIAL"
                  ? "#f59e0b"
                  : "#ef4444",
                marginBottom: 6
              }}>
                [{h.time}] → {h.app} → {h.url} → {h.state} → {h.response} ms
                {isAlert && " 🔥 ALERT"}
              </div>
            );
          })}
        </div>
      )}

      {!showHistory && (
        <>
          {/* TOP */}
          <div style={{ display: "flex", gap: 20, marginTop: 20 }}>

            {/* CARDS */}
            <div style={{ width: "70%" }}>
              <Grid container spacing={2}>
                {[
                  { label: "Total", value: instantTotal, color: "#334155", type: "ALL" },
                  { label: "UP", value: upUrls.length, color: "#10b981", type: "UP" },
                  { label: "PARTIAL", value: partialUrls.length, color: "#f59e0b", type: "PARTIAL" },
                  { label: "DOWN", value: downUrls.length, color: "#ef4444", type: "DOWN" }
                ].map((c, i) => (
                  <Grid item xs={3} key={i}>
                    <Card
                      onClick={() => setFilterType(c.type)}
                      style={{
                        background: c.color,
                        color: "white",
                        cursor: "pointer",
                        boxShadow: "0 0 15px rgba(0,0,0,0.3)"
                      }}
                    >
                      <CardContent>
                        <Typography>{c.label}</Typography>
                        <Typography variant="h4">{c.value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* PROGRESS */}
              <div style={{ display: "flex", height: 10, marginTop: 10 }}>
                <div style={{ width: `${(upUrls.length / total) * 100}%`, background: "#10b981" }} />
                <div style={{ width: `${(partialUrls.length / total) * 100}%`, background: "#f59e0b" }} />
                <div style={{ width: `${(downUrls.length / total) * 100}%`, background: "#ef4444" }} />
              </div>
            </div>

            {/* PIE */}
            <div style={{
              width: "30%",
              height: 250,
              background: "white",
              borderRadius: 10
            }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={chartData} dataKey="value" outerRadius={80} label>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* LIST */}
          <Grid container spacing={3} style={{ marginTop: 20 }}>
            {filteredList.map((app, i) => (
              <Grid item xs={4} key={i}>
                <Card>
                  <CardContent>
                    <Typography>{app.app_name}</Typography>

                    <Typography>
                      <a href={app.endpoint} target="_blank" rel="noreferrer">
                        {app.endpoint}
                      </a>
                    </Typography>

                    {/* 🔥 BLINKING STATUS */}
                    <Typography
                      style={{
                        ...(app.state !== "UP" ? blinkStyle : {}),
                        color:
                          app.state === "UP"
                            ? "#10b981"
                            : app.state === "PARTIAL"
                            ? "#f59e0b"
                            : "#ef4444",
                        fontWeight: "bold"
                      }}
                    >
                      {app.state}
                    </Typography>

                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </div>
  );
}
